import React, { useCallback, useEffect, useState } from 'react';
import { cn } from '../../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import BacktestChart from '../../../../../../components/ui/charts/BacktestChart';
import { ChartLine } from 'lucide-react';
import MediumToggle from '../../../../../../components/ui/toggles/MediumToggle';
import InvestmentAllocationComparisonDonuts from './investmentAllocations/InvestmentAllocationComparisonDonuts';
import InvestmentPolicyReleaseToggle from './InvestmentPolicyReleaseToggle';

function formatPercentage(value) {
  const number = Number(value) || 0;

  return `${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

const COMPOSITION_COLORS = {
  fi_pos: '#2F72A1',
  fi_pre_ipca: '#4497C5',
  fi_international: '#55B3E0',
  multiasset: '#6BC5DB',
  equities_br: '#82D8D5',
  equities_international: '#95DEDD',
  fii: '#A9E4E5',
  alternatives: '#CFEFF1',
  // offshore
  off_fixed_income_long_term: '#2F72A1',
  off_fixed_income_short_term: '#55B3E0',
  off_equities: '#6BC5DB',
  off_reits: '#82D8D5',
  off_commodities: '#A9E4E5',
  account_balance_percentage: '#CFEFF1',
  // unclassified
  unclassified: '#E6FAFB',
};

const MODALITY_OPTIONS = ['NACIONAL', 'INTERNACIONAL'];

const MODALITY_TO_PARAM = {
  NACIONAL: 'national',
  INTERNACIONAL: 'international',
};

const InvestmentAllocations = ({ clientId, scope, canWrite }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedModality, setSelectedModality] = useState('NACIONAL');

  const fetchData = useCallback(() => {
    setLoading(true);

    const modality = MODALITY_TO_PARAM[selectedModality] || 'national';

    fetch(
      `/app/planner/client/${clientId}/dashboard/investment_strategy/investment_policy?scope=${scope}&modality=${modality}`
    )
      .then(async (res) => {
        const responseData = await res.json();

        if (!res.ok) {
          throw responseData;
        }

        return responseData;
      })
      .then((responseData) => {
        const camelData = camelizeKeys(responseData);

        setData(camelData);

        console.log('Fetched investment policy data:', camelData);
      })
      .catch((error) => {
        console.error('Erro ao buscar estratégia de investimentos:', error);
        setData({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, scope, selectedModality]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between bg-white rounded-custom20 px-10 py-5 min-w-0">
        <div className="flex items-center gap-3">
          <ChartLine className="text-tealMedium" size={20} strokeWidth={1.75} />

          <span className="text-xl font-medium text-blueDarker">Alocação Patrimonial</span>

          <MediumToggle
            options={MODALITY_OPTIONS}
            selected={selectedModality}
            onChange={(value) => {
              setSelectedModality(value);
            }}
          />
        </div>

        <InvestmentPolicyReleaseToggle clientId={clientId} canWrite={canWrite} />
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: '1.7fr 1fr' }}>
        <div className="flex flex-col gap-3 bg-white rounded-custom20 px-5 py-5 min-w-0">
          {loading ? (
            <div className="flex h-96 items-center justify-center rounded-custom20 border border-gray-100">
              <span className="text-sm text-gray-400">Carregando...</span>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <h3 className="text-lg font-semibold text-blueDarker px-5 py-1">Composição da carteira inicial</h3>
              <InvestmentAllocationComparisonDonuts
                currentComposition={data.currentComposition}
                suggestedComposition={data.suggestedComposition}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 bg-white rounded-custom20 px-5 py-5 min-w-0">
          {loading ? (
            <div className="flex h-96 items-center justify-center rounded-custom20 border border-gray-100">
              <span className="text-sm text-gray-400">Carregando...</span>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <h3 className="text-lg font-semibold text-blueDarker px-5 py-1">Carteira inicial</h3>
              <div className="flex flex-col gap-3 text-base">
                {data.suggestedComposition?.items?.map((item, index) => (
                  <div
                    className="flex items-center justify-between bg-blueLight px-5 py-4"
                    style={{ borderRadius: 12 }}
                  >
                    <div className="flex gap-5 items-center">
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 6,
                          backgroundColor: COMPOSITION_COLORS[item.key] || '#CFEFF1',
                          flexShrink: 0,
                        }}
                      />
                      <span>{item.label}</span>
                    </div>
                    <span className="font-semibold" style={{ color: COMPOSITION_COLORS[item.key] || '#CFEFF1' }}>
                      {formatPercentage(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentAllocations;
